import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Mail, MailOpen, ArrowLeft, Clock } from "lucide-react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function InboxPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/inbox`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const openMessage = async (message) => {
    setSelectedMessage(message);
    setDialogOpen(true);
    
    // Mark as read
    if (!message.is_read) {
      try {
        await axios.post(`${API}/messages/${message.message_id}/read`, {}, { withCredentials: true });
        setMessages(prev => prev.map(m => 
          m.message_id === message.message_id ? { ...m, is_read: true } : m
        ));
      } catch (error) {
        console.error("Error marking message as read:", error);
      }
    }
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  return (
    <Layout>
      <section className="gradient-hero py-12" data-testid="inbox-hero">
        <div className="container-custom">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-heading">Inbox</h1>
              <p className="text-white/80">
                {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8" data-testid="inbox-section">
        <div className="container-custom max-w-3xl">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-lg p-6 h-20 animate-pulse"></div>
              ))}
            </div>
          ) : messages.length > 0 ? (
            <div className="space-y-3">
              {messages.map(message => (
                <button
                  key={message.message_id}
                  onClick={() => openMessage(message)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all hover:shadow-md ${
                    message.is_read 
                      ? 'bg-white border-slate-200' 
                      : 'bg-awareness-blue/5 border-awareness-blue'
                  }`}
                  data-testid={`message-${message.message_id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.is_read ? 'bg-slate-100' : 'bg-awareness-blue'
                    }`}>
                      {message.is_read 
                        ? <MailOpen className="w-5 h-5 text-slate-500" />
                        : <Mail className="w-5 h-5 text-white" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className={`font-semibold truncate ${message.is_read ? 'text-slate-700' : 'text-deep-blue'}`}>
                          {message.subject}
                        </h3>
                        <span className="text-xs text-slate-500 flex-shrink-0">
                          {format(new Date(message.created_at), "MMM d")}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">From: {message.sender_name}</p>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-1">{message.content}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg p-12 text-center">
              <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No Messages Yet</h3>
              <p className="text-slate-500">Messages from Blue vs Blue will appear here.</p>
            </div>
          )}
        </div>
      </section>

      {/* Message Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">{selectedMessage?.subject}</DialogTitle>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-sm text-slate-500 pb-4 border-b">
                <span>From: <span className="font-medium text-slate-700">{selectedMessage.sender_name}</span></span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {format(new Date(selectedMessage.created_at), "MMMM d, yyyy 'at' h:mm a")}
                </span>
              </div>
              
              <div className="prose prose-sm max-w-none">
                {selectedMessage.content.split('\n').map((paragraph, index) => (
                  paragraph.trim() && (
                    <p key={index} className="text-slate-600 mb-4">
                      {paragraph}
                    </p>
                  )
                ))}
              </div>

              {selectedMessage.is_broadcast && (
                <div className="bg-awareness-blue/10 text-awareness-blue text-sm px-3 py-2 rounded-lg">
                  This message was sent to all supporters
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
